// src/brandDomains.ts
function normalizeBrandDomain(input) {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return null;
  let host = trimmed;
  try {
    host = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`).hostname;
  } catch {
    return null;
  }
  host = host.replace(/^www\./, "").replace(/\.$/, "");
  if (!host.includes(".") || host.includes(" ")) return null;
  return host;
}
function brandOwnedDomains(brand) {
  return normalizedDomains([
    brand.website,
    brand.brandfetch?.domain,
    ...brand.domains ?? [],
    ...brand.researchedDomains ?? []
  ]);
}
function isBrandOwnedHost(brand, urlOrHost) {
  return isHostWithin(urlOrHost, brandOwnedDomains(brand));
}
function brandTrustedDomains(brand) {
  return normalizedDomains(brand.trustedDomains ?? []);
}
function isBrandTrustedHost(brand, urlOrHost) {
  return isHostWithin(urlOrHost, brandTrustedDomains(brand));
}
function isHostWithin(urlOrHost, domains) {
  const host = normalizeBrandDomain(urlOrHost);
  if (!host) return false;
  return domains.some((domain) => host === domain || host.endsWith(`.${domain}`));
}
function withBrandDomainAdded(brand, input) {
  const domain = normalizeBrandDomain(input);
  if (!domain) return null;
  if (brandOwnedDomains(brand).includes(domain) || brandTrustedDomains(brand).includes(domain)) return null;
  return withBrandDomainPinned(brand, domain);
}
function withBrandDomainPinned(brand, input) {
  const domain = normalizeBrandDomain(input);
  if (!domain) return null;
  const current = brand.domains ?? [];
  if (current.includes(domain)) return null;
  return [...current, domain].sort();
}
function withBrandDomainRemoved(brand, domain) {
  return withoutListedDomain(brand.domains ?? [], domain);
}
function withResearchedDomainRemoved(brand, domain) {
  return withoutListedDomain(brand.researchedDomains ?? [], domain);
}
function withResearchedDomainsAdded(brand, inputs) {
  const known = /* @__PURE__ */ new Set([...brandOwnedDomains(brand), ...brandTrustedDomains(brand)]);
  const added = normalizedDomains([...inputs]).filter((domain) => !known.has(domain));
  if (added.length === 0) return null;
  return [...brand.researchedDomains ?? [], ...added].sort();
}
function withTrustedDomainAdded(brand, input) {
  const domain = normalizeBrandDomain(input);
  if (!domain) return null;
  if (isBrandOwnedHost(brand, domain) || brandTrustedDomains(brand).includes(domain)) return null;
  return [...brand.trustedDomains ?? [], domain].sort();
}
function withTrustedDomainRemoved(brand, domain) {
  return withoutListedDomain(brand.trustedDomains ?? [], domain);
}
function normalizedDomains(candidates) {
  const domains = /* @__PURE__ */ new Set();
  for (const candidate of candidates) {
    const normalized = candidate ? normalizeBrandDomain(candidate) : null;
    if (normalized) domains.add(normalized);
  }
  return [...domains].sort();
}
function withoutListedDomain(current, domain) {
  const normalized = normalizeBrandDomain(domain);
  if (!normalized) return null;
  if (!current.includes(normalized)) return null;
  return current.filter((d) => d !== normalized).sort();
}

// src/brandLines.ts
import { dedupeStrings, generateOcrVariants as generateOcrVariants2, normalizeBrandName as normalizeBrandName2 } from "@rock/util";

// src/sharedNameplates.ts
import { confusionKey, generateOcrVariants, normalizeBrandName } from "@rock/util";
var NO_SHARED_NAMEPLATES = [];
function sharedNameplateForKey(key, table) {
  return key ? byConfusionKey(table).get(confusionKey(key)) : void 0;
}
function isSharedNameplateName(name, table) {
  return !!sharedNameplateForKey(normalizeBrandName(name).key, table);
}
function isSharedNameplateKey(key, table) {
  return !!sharedNameplateForKey(key, table);
}
function routeSharedNameplate(name, spaceId, table) {
  if (!spaceId) return void 0;
  const entry = sharedNameplateForKey(normalizeBrandName(name).key, table);
  return entry?.routes.find((route) => route.spaceId === spaceId)?.brandId;
}
function isSharedNameplateSide(brandId, table) {
  return table.some(
    (entry) => entry.sides.includes(brandId) || entry.routes.some((route) => route.brandId === brandId)
  );
}
function sharedNameplateEntries(nameplates) {
  return nameplates.map((nameplate) => ({
    key: nameplate.key,
    sides: nameplate.sides,
    routes: nameplate.routes.map(({ spaceId, brandId }) => ({ spaceId, ...brandId ? { brandId } : {} }))
  }));
}
function planSharedNameplate(input, table) {
  const key = normalizeBrandName(input.name).key;
  if (!key) return { refusal: "The name folds to nothing." };
  if (input.existing && input.existing.key !== key) return { refusal: "A nameplate\u2019s key can\u2019t change." };
  if (!input.reason.trim()) return { refusal: "Say why the name is shared; the deny docs show it." };
  const sides = [...new Set(input.sides)];
  if (sides.length < 2) return { refusal: "A shared nameplate needs at least two qualified brands." };
  const other = table.find((entry) => entry.key !== key && entry.sides.some((side) => sides.includes(side)));
  if (other) return { refusal: `A side already belongs to the ${other.key} nameplate.` };
  const spaces = /* @__PURE__ */ new Set();
  for (const route of input.routes) {
    if (route.brandId && !sides.includes(route.brandId))
      return { refusal: "A route must point at one of the sides." };
    if (spaces.has(route.spaceId)) return { refusal: `Two routes for the ${route.spaceId} space.` };
    spaces.add(route.spaceId);
  }
  const nameplate = {
    object: "shared-nameplate",
    schema: 1,
    id: key,
    key,
    reason: input.reason.trim(),
    sides,
    routes: input.routes,
    createdAt: input.existing?.createdAt ?? input.now,
    updatedAt: input.now
  };
  return { plan: { nameplate, denials: planSharedNameplateDenials(nameplate, input.now) } };
}
function planSharedNameplateDenials(nameplate, now) {
  return [...generateOcrVariants(nameplate.key)].map((variant) => ({
    object: "brand-alias",
    schema: 1,
    id: variant,
    canonicalKey: nameplate.key,
    denied: true,
    reason: nameplate.reason,
    nameplateKey: nameplate.key,
    createdAt: now,
    updatedAt: now
  }));
}
var INDEX_CACHE = /* @__PURE__ */ new WeakMap();
function byConfusionKey(table) {
  const cached = INDEX_CACHE.get(table);
  if (cached) return cached;
  const index = new Map(table.map((entry) => [confusionKey(entry.key), entry]));
  INDEX_CACHE.set(table, index);
  return index;
}

// src/brandLines.ts
function planBrandLineAddition(brand, input, sharedNameplates) {
  const displayName = lineDisplayName(brand, input.displayName, sharedNameplates);
  const lineId = brandLineIdFor(displayName);
  if (!lineId) return { refusal: "The line name folds to nothing." };
  const identifying = brandQualifyingKeys(brand, sharedNameplates, input.absorbAlias);
  if (identifying.has(lineId)) {
    return { refusal: "That is the brand\u2019s own name; a line is a product family within it." };
  }
  const existing = brand.lines?.find((line2) => line2.id === lineId);
  const line = existing ?? { id: lineId, displayName };
  const { keys, skippedNames } = lineKeys(lineId, identifying, input, sharedNameplates, !!existing);
  const aliasDocs = lineAliasDocs(keys, brand, lineId, input.now);
  if (existing && aliasDocs.length === 0) {
    return {
      refusal: `${brand.canonicalName} already has a ${line.displayName} line; nothing new to add.`,
      existingLine: existing
    };
  }
  const absorbedKey = input.absorbAlias ? normalizeBrandName2(input.absorbAlias).key : void 0;
  const brandAliases = absorbedKey ? brand.aliases.filter((alias) => normalizeBrandName2(alias).key !== absorbedKey) : void 0;
  return {
    plan: {
      line,
      aliasDocs,
      brandLines: existing ? brand.lines : [...brand.lines ?? [], line],
      ...brandAliases && brandAliases.length !== brand.aliases.length ? { brandAliases } : {},
      skippedNames
    }
  };
}
function planBrandLineRemoval(brand, lineId, ownedAliasDocs) {
  if (!brand.lines?.some((line) => line.id === lineId)) return null;
  return {
    deleteKeys: ownedAliasDocs.filter((doc) => doc.brandId === brand.id && doc.lineId === lineId).map((doc) => doc.id),
    brandLines: brand.lines.filter((line) => line.id !== lineId)
  };
}
function brandLineKeyDocs(brand, lineId, sharedNameplates, now) {
  return lineAliasDocs(lineBaseKeys(brandQualifyingKeys(brand, sharedNameplates), lineId), brand, lineId, now);
}
function brandLineIdOn(brand, typed, sharedNameplates) {
  return brandLineIdFor(lineDisplayName(brand, typed, sharedNameplates));
}
function lineNameFromAlias(brand, alias, sharedNameplates = []) {
  const own = brandQualifyingKeys(brand, sharedNameplates);
  if (own.has(normalizeBrandName2(alias).key)) return void 0;
  const words = normalizeBrandName2(alias).display.split(" ");
  const names = [...brandIdentifyingNames(brand), ...brand.displayName ? [brand.displayName] : []];
  const prefixes = [
    // Both spellings of each name: as printed ("HUNTER FAN COMPANY") and folded ("HUNTER FAN") — the
    // fold drops a trailing corporate word that the alias keeps in the middle.
    ...names.flatMap((name) => [uppercaseWords(name), normalizeBrandName2(name).display.split(" ")]),
    ...sharedKeysFor(brand.id, sharedNameplates).map((key) => [key])
  ].sort((a, b) => b.length - a.length);
  for (const prefix of prefixes) {
    if (prefix.length >= words.length) continue;
    if (prefix.every((word, i) => word === words[i])) return titleCase(words.slice(prefix.length).join(" "));
  }
  return void 0;
}
function brandIdentifyingNames(brand) {
  return dedupeStrings([brand.canonicalName, ...brand.aliases]);
}
function lineKeys(lineId, identifying, input, sharedNameplates, existed) {
  const keys = new Set(existed ? [] : lineBaseKeys(identifying, lineId));
  const skippedNames = [];
  const extras = dedupeStrings([...input.extraNames ?? [], ...input.absorbAlias ? [input.absorbAlias] : []]);
  for (const extra of extras) {
    const key = normalizeBrandName2(extra).key;
    const qualified = !!key && key !== lineId && !identifying.has(key) && [...identifying].some((brandKey) => key.includes(brandKey));
    if (qualified && !isSharedNameplateKey(key, sharedNameplates)) keys.add(key);
    else skippedNames.push(extra);
  }
  return { keys, skippedNames };
}
function brandLineIdFor(displayName) {
  return normalizeBrandName2(displayName).key;
}
function lineDisplayName(brand, typed, sharedNameplates) {
  const stripped = lineNameFromAlias(brand, typed, sharedNameplates);
  if (stripped) return stripped;
  const trimmed = typed.trim();
  return trimmed === trimmed.toUpperCase() ? titleCase(trimmed) : trimmed;
}
function brandQualifyingKeys(brand, sharedNameplates, absorbAlias) {
  const absorbed = absorbAlias ? nameKeys(absorbAlias) : [];
  const keys = brandIdentifyingNames(brand).flatMap(nameKeys).filter((key) => key && !absorbed.includes(key));
  return /* @__PURE__ */ new Set([...keys, ...sharedKeysFor(brand.id, sharedNameplates)]);
}
function nameKeys(name) {
  return dedupeStrings([normalizeBrandName2(name).key, uppercaseWords(name).join("")]);
}
function sharedKeysFor(brandId, sharedNameplates) {
  return sharedNameplates.filter((entry) => entry.sides.includes(brandId)).map((entry) => entry.key);
}
function lineBaseKeys(qualifying, lineId) {
  return [...qualifying].map((brandKey) => `${brandKey}${lineId}`);
}
function lineAliasDocs(keys, brand, lineId, now) {
  const variants = new Set([...keys].flatMap((key) => [...generateOcrVariants2(key)]));
  return [...variants].map((variant) => ({
    object: "brand-alias",
    schema: 1,
    id: variant,
    brandId: brand.id,
    canonicalKey: brand.canonicalKey,
    lineId,
    createdAt: now,
    updatedAt: now
  }));
}
function uppercaseWords(name) {
  return name.toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
}
function titleCase(words) {
  return words.toLowerCase().split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

// src/brandPicks.ts
import { normalizeBrandName as normalizeBrandName3 } from "@rock/util";
function brandPickNamesText(brand, ref, text) {
  const key = text ? normalizeBrandName3(text).key : "";
  if (!key) return false;
  const names = /* @__PURE__ */ new Set([
    brand.canonicalKey,
    normalizeBrandName3(brand.displayName ?? "").key,
    ...brand.aliases.map((alias) => normalizeBrandName3(alias).key)
  ]);
  const line = ref.lineId ? brand.lines?.find((candidate) => candidate.id === ref.lineId) : void 0;
  if (line) names.add(normalizeBrandName3(`${brand.displayName ?? brand.canonicalName} ${line.displayName}`).key);
  return names.has(key);
}
function brandPickStands(brand, ref, text, nameplates) {
  return brandPickNamesText(brand, ref, text) && !isSharedNameplateKey(brand.canonicalKey, nameplates);
}

// src/brandRequests.ts
import { BRAND_REQUEST_SIGHTING_CAP } from "@rock/types";
import { confusionKey as confusionKey2, dedupeStrings as dedupeStrings3, isMeaningfulBrandName as isMeaningfulBrandName2, normalizeBrandName as normalizeBrandName5 } from "@rock/util";

// src/planBrandCreation.ts
import {
  dedupeStrings as dedupeStrings2,
  generateOcrVariants as generateOcrVariants3,
  isMeaningfulBrandName,
  normalizeBrandName as normalizeBrandName4
} from "@rock/util";
function isCatalogableBrandName(name, sharedNameplates) {
  return foldCatalogableBrandName(name, sharedNameplates) !== null;
}
function planBrandCreation(name, { brandId, now, sharedNameplates, displayName: printedName }) {
  const normalized = foldCatalogableBrandName(name, sharedNameplates);
  if (!normalized) return null;
  const canonicalKey = normalized.key;
  const displayName = normalized.display;
  const printed = printedName?.trim();
  const brand = {
    object: "brand",
    schema: 1,
    id: brandId,
    canonicalName: displayName,
    canonicalKey,
    ...printed && printed !== displayName ? { displayName: printed } : {},
    // Display forms only — the folded key lives in the `brand-aliases` lookup docs, not this list.
    aliases: [displayName],
    reviewStatus: "unreviewed",
    createdAt: now,
    updatedAt: now
  };
  return { brand, aliases: aliasDocsForKey(canonicalKey, brand, now) };
}
function planBrandAliasAddition(brand, aliasName, now, sharedNameplates) {
  const normalized = foldCatalogableBrandName(aliasName, sharedNameplates);
  if (!normalized) return null;
  const key = normalized.key;
  const display = normalized.display;
  return {
    display,
    key,
    aliasDocs: aliasDocsForKey(key, brand, now),
    // Display forms only — the folded key lives in the `brand-aliases` lookup docs, not this list.
    brandAliases: dedupeStrings2([...brand.aliases, display])
  };
}
function planBrandAliasRemoval(brand, aliasName, ownedAliasDocs) {
  const normalized = normalizeBrandName4(aliasName);
  if (!normalized.key || normalized.key === brand.canonicalKey) return null;
  const owned = new Set(
    ownedAliasDocs.filter((doc) => doc.brandId === brand.id && !doc.lineId).map((doc) => doc.id)
  );
  const removed = /* @__PURE__ */ new Set([normalized.display, normalized.key, aliasName.trim()]);
  const brandAliases = brand.aliases.filter((alias) => !removed.has(alias));
  const protectedKeys = new Set(generateOcrVariants3(brand.canonicalKey));
  for (const alias of brandAliases) {
    const aliasKey = normalizeBrandName4(alias).key;
    if (!aliasKey) continue;
    for (const variant of generateOcrVariants3(aliasKey)) protectedKeys.add(variant);
  }
  const deleteKeys = [...generateOcrVariants3(normalized.key)].filter((variant) => !protectedKeys.has(variant) && owned.has(variant)).map((variant) => variant);
  if (deleteKeys.length === 0 && brandAliases.length === brand.aliases.length) return null;
  return { deleteKeys, brandAliases };
}
function foldCatalogableBrandName(name, sharedNameplates) {
  if (!isMeaningfulBrandName(name)) return null;
  const normalized = normalizeBrandName4(name);
  if (!normalized.key || !isMeaningfulBrandName(normalized.display)) return null;
  if (isSharedNameplateKey(normalized.key, sharedNameplates)) return null;
  return normalized;
}
function aliasDocsForKey(key, brand, now) {
  return [...generateOcrVariants3(key)].map((variant) => ({
    object: "brand-alias",
    schema: 1,
    id: variant,
    brandId: brand.id,
    canonicalKey: brand.canonicalKey,
    createdAt: now,
    updatedAt: now
  }));
}

// src/brandRequests.ts
function brandRequestIdFor(name) {
  const key = normalizeBrandName5(name).key;
  return key ? key : void 0;
}
function catalogRequestIdFor(name, sharedNameplates) {
  if (!isMeaningfulBrandName2(name) || !isCatalogableBrandName(name, sharedNameplates)) return void 0;
  return brandRequestIdFor(name);
}
function applyBrandRequestSighting(existing, input, now) {
  const { id, name, sighting } = input;
  const base = existing ?? {
    object: "brand-request",
    schema: 1,
    id,
    brand: normalizeBrandName5(name).display || name.trim(),
    status: "pending",
    sightings: [],
    sightingCount: 0,
    sourceCounts: {},
    spaceIds: [],
    firstSeenAt: sighting.at
  };
  const matches = base.sightings.filter((seen) => sameSighting(seen, sighting));
  const repeat = matches.length > 0;
  const others = base.sightings.filter((seen) => !sameSighting(seen, sighting));
  const seenAgain = base.status === "parked" && sighting.provenance === "observed" && matches.every((seen) => seen.provenance === "researched");
  return {
    ...base,
    ...seenAgain ? { status: "pending", ...base.parked ? { parked: { ...base.parked, seenAgain: sighting } } : {} } : {},
    sightings: [sighting, ...others].slice(0, BRAND_REQUEST_SIGHTING_CAP),
    sightingCount: repeat ? base.sightingCount : base.sightingCount + 1,
    sourceCounts: repeat ? base.sourceCounts : { ...base.sourceCounts, [sighting.source]: (base.sourceCounts[sighting.source] ?? 0) + 1 },
    spaceIds: sighting.spaceId ? dedupeStrings3([...base.spaceIds, sighting.spaceId]) : base.spaceIds,
    firstSeenAt: base.firstSeenAt ?? sighting.at,
    lastSeenAt: sighting.at,
    updatedAt: now
  };
}
function sameSighting(a, b) {
  if (a.source !== b.source) return false;
  if (a.homeId && b.homeId && a.homeId !== b.homeId) return false;
  if (a.modelId || b.modelId) return a.modelId === b.modelId;
  if (a.recallId || b.recallId) return a.recallId === b.recallId;
  if (a.equipmentId || b.equipmentId) return a.equipmentId === b.equipmentId;
  return false;
}
var GENERIC_EXTENSION_WORDS = /* @__PURE__ */ new Set([
  "APPLIANCE",
  "APPLIANCES",
  "AIR",
  "BRANDS",
  "COOLING",
  "ELECTRIC",
  "ELECTRONICS",
  "ENERGY",
  "EQUIPMENT",
  "GROUP",
  "HEATING",
  "HOME",
  "HVAC",
  "INDUSTRIES",
  "INTERNATIONAL",
  "MANUFACTURING",
  "PLUMBING",
  "PRODUCTS",
  "REFRIGERATION",
  "SYSTEMS",
  "TECHNOLOGIES",
  "USA",
  "WATER"
]);
function suggestBrandRequestResolution(name, catalog, sharedNameplates) {
  const { key, display } = normalizeBrandName5(name);
  if (!key) return [];
  if (isSharedNameplateKey(key, sharedNameplates)) {
    return [{ kind: "shared", detail: "A shared nameplate: two companies print this name." }];
  }
  const words = display.split(" ");
  const suggestions = [];
  const seen = /* @__PURE__ */ new Set();
  const add = (brand, kind, detail) => {
    if (seen.has(`${brand.id}:${kind}`)) return;
    seen.add(`${brand.id}:${kind}`);
    suggestions.push({ kind, brandId: brand.id, brandName: brand.displayName ?? brand.canonicalName, detail });
  };
  for (const brand of catalog) {
    if (brand.reviewStatus === "junk" || brand.mergedInto) continue;
    for (const brandName of brandNames(brand)) {
      const brandKey = normalizeBrandName5(brandName).key;
      if (!brandKey) continue;
      if (brandKey === key) {
        add(brand, "alias", `The same name as ${brandName}.`);
        continue;
      }
      if (confusionKey2(brandKey) === confusionKey2(key)) {
        add(brand, "alias", `Reads like a misspelling of ${brandName}.`);
        continue;
      }
      const extra = extensionWords(words, brandName);
      if (!extra) continue;
      if (extra.every((word) => GENERIC_EXTENSION_WORDS.has(word))) {
        add(brand, "alias", `${brandName} plus a generic word (${extra.join(" ")}).`);
      } else {
        add(brand, "line", `Extends ${brandName}: could be a product line.`);
      }
    }
  }
  if (suggestions.length === 0 && words.length > 1) {
    const first = words[0];
    for (const brand of catalog) {
      if (brand.reviewStatus === "junk" || brand.mergedInto) continue;
      if (normalizeBrandName5(brand.canonicalName).display.split(" ")[0] === first) {
        add(brand, "line", `Shares its first word with ${brand.displayName ?? brand.canonicalName}.`);
      }
    }
  }
  return rankSuggestions(suggestions);
}
function brandAlreadyNaming(name, catalog) {
  const { key } = normalizeBrandName5(name);
  if (!key) return void 0;
  const naming = catalog.filter(
    (brand) => brand.reviewStatus !== "junk" && !brand.mergedInto && brandNames(brand).some((brandName) => normalizeBrandName5(brandName).key === key)
  );
  return naming.length === 1 ? naming[0] : void 0;
}
function brandNames(brand) {
  return dedupeStrings3([brand.canonicalName, ...brand.aliases]);
}
function extensionWords(requestWords, brandName) {
  const brandJoined = normalizeBrandName5(brandName).display.split(" ").join("");
  let consumed = "";
  let i = 0;
  while (i < requestWords.length && consumed.length < brandJoined.length) {
    consumed += requestWords[i];
    i += 1;
  }
  if (consumed !== brandJoined || i >= requestWords.length) return void 0;
  return requestWords.slice(i);
}
var KIND_RANK = { shared: 0, alias: 1, line: 2 };
function rankSuggestions(suggestions) {
  return [...suggestions].sort((a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind]);
}

// src/brandResearch.ts
import { isBrandRequestOpen } from "@rock/types";
import { normalizeBrandName as normalizeBrandName7 } from "@rock/util";

// src/searchBrands.ts
import { normalizeBrandName as normalizeBrandName6, normalizeNameKey } from "@rock/util";
var MIN_QUERY_LENGTH = 2;
var MIN_SUBSTRING_QUERY_LENGTH = 3;
var DEFAULT_LIMIT = 8;
var AMBIGUITY_BOOST = 25;
var SCORE = {
  exactCanonical: 100,
  exactAlias: 90,
  prefixCanonical: 80,
  prefixAlias: 70,
  wordPrefixCanonical: 60,
  wordPrefixAlias: 50,
  substringCanonical: 40
};
function foldBrandCatalog(index) {
  return {
    version: index.version,
    sharedNameplates: index.sharedNameplates ?? NO_SHARED_NAMEPLATES,
    entries: index.entries.map((entry) => {
      const canonicalKey = normalizeBrandName6(entry.canonicalName).key;
      const nameKey = normalizeBrandName6(entry.name).key;
      return {
        entry,
        canonicalKey,
        canonicalWordKeys: laterWordKeys(entry.canonicalName),
        nameKey,
        aliasKeys: (entry.aliases ?? []).map((alias) => normalizeBrandName6(alias).key),
        aliasWordKeys: (entry.aliases ?? []).map(laterWordKeys),
        lines: (entry.lines ?? []).map((line) => {
          const label = `${entry.name} ${line.displayName}`;
          return { id: line.id, label, key: normalizeBrandName6(label).key, wordKeys: laterWordKeys(label) };
        }),
        // Only a genuinely distinct catalog name is worth showing — "WHIRLPOOL" next to
        // "Whirlpool" would be noise, but "MAYTAG APPLIANCES" next to "Maytag" disambiguates.
        qualifier: canonicalKey !== nameKey ? entry.canonicalName : void 0
      };
    })
  };
}
function resolveExactBrandId(catalog, value) {
  const key = normalizeBrandName6(value).key;
  if (!key) return void 0;
  let found;
  for (const { entry, nameKey, canonicalKey } of catalog.entries) {
    if (nameKey === key || canonicalKey === key) {
      if (found) return void 0;
      found = entry.id;
    }
  }
  return found;
}
function searchBrands(catalog, query, { spaceId, limit = DEFAULT_LIMIT } = {}) {
  const queryKey = normalizeBrandName6(query).key;
  if (queryKey.length < MIN_QUERY_LENGTH) return [];
  const routedId = routeSharedNameplate(query, spaceId, catalog.sharedNameplates);
  const scored = [];
  for (const candidate of catalog.entries) {
    const boost = routedId && candidate.entry.id === routedId ? AMBIGUITY_BOOST : 0;
    const hit = scoreEntry(candidate, queryKey);
    if (hit) scored.push({ suggestion: hit.suggestion, score: hit.score + boost });
    for (const line of scoreLines(candidate, queryKey)) {
      scored.push({ suggestion: line.suggestion, score: line.score + boost });
    }
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.suggestion);
}
function scoreEntry(candidate, queryKey) {
  const { entry, canonicalKey, canonicalWordKeys, qualifier } = candidate;
  const base = { id: entry.id, name: entry.name, ...qualifier ? { qualifier } : {} };
  const nameHit = (score) => ({ suggestion: base, score });
  const aliasHit = ({ index, score }) => ({
    suggestion: { ...base, matchedAlias: entry.aliases?.[index] },
    score
  });
  if (canonicalKey === queryKey) return nameHit(SCORE.exactCanonical);
  const alias = bestAliasMatch(candidate, queryKey);
  if (alias?.score === SCORE.exactAlias) return aliasHit(alias);
  if (canonicalKey.startsWith(queryKey)) return nameHit(SCORE.prefixCanonical);
  if (alias?.score === SCORE.prefixAlias) return aliasHit(alias);
  const midName = queryKey.length >= MIN_SUBSTRING_QUERY_LENGTH;
  if (midName && canonicalWordKeys.some((word) => word.startsWith(queryKey))) {
    return nameHit(SCORE.wordPrefixCanonical);
  }
  if (alias) return aliasHit(alias);
  if (midName && canonicalKey.includes(queryKey)) return nameHit(SCORE.substringCanonical);
  return void 0;
}
function scoreLines(candidate, queryKey) {
  const hits = [];
  const allowWordPrefix = queryKey.length >= MIN_SUBSTRING_QUERY_LENGTH;
  for (const line of candidate.lines) {
    let score;
    if (line.key === queryKey) score = SCORE.exactAlias;
    else if (line.key.startsWith(queryKey)) score = SCORE.prefixAlias;
    else if (allowWordPrefix && line.wordKeys.some((word) => word.startsWith(queryKey)))
      score = SCORE.wordPrefixAlias;
    if (score === void 0) continue;
    hits.push({
      suggestion: {
        id: candidate.entry.id,
        name: line.label,
        lineId: line.id,
        qualifier: candidate.entry.canonicalName
      },
      score
    });
  }
  return hits;
}
function bestAliasMatch({ aliasKeys, aliasWordKeys }, queryKey) {
  let best;
  const consider = (index, score) => {
    if (!best || score > best.score) best = { index, score };
  };
  const allowWordPrefix = queryKey.length >= MIN_SUBSTRING_QUERY_LENGTH;
  for (const [i, key] of aliasKeys.entries()) {
    if (key === queryKey) return { index: i, score: SCORE.exactAlias };
    if (key.startsWith(queryKey)) consider(i, SCORE.prefixAlias);
    else if (allowWordPrefix && aliasWordKeys[i].some((word) => word.startsWith(queryKey))) {
      consider(i, SCORE.wordPrefixAlias);
    }
  }
  return best;
}
function laterWordKeys(name) {
  const words = normalizeBrandName6(name).display.split(" ");
  return words.slice(1).map((_, i) => normalizeNameKey(words.slice(i + 1).join(" ")));
}

// src/brandResearch.ts
var BRAND_RESEARCH_HINT_LIMIT = 10;
function brandResearchHints(name, sightingTexts, catalog, brands) {
  const hints = /* @__PURE__ */ new Map();
  const add = (brandId, why) => {
    const entry = brandId && catalog.entries.find((candidate) => candidate.entry.id === brandId);
    if (entry && !hints.has(entry.entry.id))
      hints.set(entry.entry.id, { brandId: entry.entry.id, name: entry.entry.name, why });
  };
  for (const suggestion of searchBrands(catalog, name, { limit: 5 })) add(suggestion.id, "a similar name");
  for (const hint of suggestBrandRequestResolution(name, [...brands], catalog.sharedNameplates)) {
    add(hint.brandId, hint.detail);
  }
  for (const phrase of sightingPhrases(sightingTexts)) {
    add(resolveExactBrandId(catalog, phrase), `named where it was seen ("${phrase}")`);
  }
  return [...hints.values()].slice(0, BRAND_RESEARCH_HINT_LIMIT);
}
function sightingPhrases(texts) {
  const phrases = [];
  for (const text of texts) {
    const words = text.split(/[^\p{L}\p{N}&'-]+/u).filter(Boolean);
    for (let size = 1; size <= 3; size++) {
      for (let start = 0; start + size <= words.length; start++) {
        const phrase = words.slice(start, start + size).join(" ");
        if (normalizeBrandName7(phrase).key.length >= 3) phrases.push(phrase);
      }
    }
  }
  return phrases;
}
var MIN_CONTAINED_KEY_LENGTH = 4;
function similarPendingRequests(request, pending) {
  return pending.filter((other) => isBrandRequestOpen(other) && other.id !== request.id).filter((other) => contains(other.id, request.id) || contains(request.id, other.id)).slice(0, BRAND_RESEARCH_HINT_LIMIT);
}
function contains(outer, inner) {
  return inner.length >= MIN_CONTAINED_KEY_LENGTH && outer.includes(inner);
}
function resolveCatalogBrandId(catalog, name) {
  const exact = resolveExactBrandId(catalog, name);
  if (exact) return exact;
  const key = normalizeBrandName7(name).key;
  if (!key) return void 0;
  const owners = catalog.entries.filter((candidate) => candidate.aliasKeys.includes(key));
  return owners.length === 1 ? owners[0].entry.id : void 0;
}
var NOT_BRAND_OWNED_SITES = [
  "aliexpress.com",
  "amazon.com",
  "bestbuy.com",
  "cpsc.gov",
  "ebay.com",
  "facebook.com",
  "homedepot.com",
  "instagram.com",
  "linkedin.com",
  "lowes.com",
  "manualslib.com",
  "manualzz.com",
  "manua.ls",
  "reddit.com",
  "target.com",
  "temu.com",
  "walmart.com",
  "wayfair.com",
  "wikipedia.org",
  "youtube.com"
];
function judgeResearchedDomain(host, probe, brandName, ownerOf) {
  if (isHostWithin(host, NOT_BRAND_OWNED_SITES))
    return { verdict: "rejected", detail: "a retailer, marketplace or mirror" };
  const ownerBrandId = ownerOf(host);
  if (ownerBrandId) return { verdict: "rejected", detail: "already another brand\u2019s", ownerBrandId };
  if (!probe || probe.status === "error") {
    return { verdict: "rejected", detail: `unreachable${probe?.error ? ` (${probe.error})` : ""}` };
  }
  if (probe.status >= 400) return { verdict: "doubtful", detail: `answers ${probe.status}` };
  const finalHost = normalizeBrandDomain(probe.finalUrl);
  if (finalHost && siteOf(finalHost) !== siteOf(host)) {
    return { verdict: "doubtful", detail: `redirects to ${finalHost}` };
  }
  if (!namesBrand(probe.text, brandName)) return { verdict: "doubtful", detail: "its page never names the brand" };
  return { verdict: "ok", detail: "answers and names the brand" };
}
function brandDomainOwnerLookup(brands) {
  const owners = /* @__PURE__ */ new Map();
  for (const brand of brands) {
    if (brand.mergedInto || brand.reviewStatus === "junk") continue;
    for (const domain of brandOwnedDomains(brand)) if (!owners.has(domain)) owners.set(domain, brand.id);
  }
  return (input) => {
    const labels = (normalizeBrandDomain(input) ?? "").split(".");
    for (let start = 0; start < labels.length - 1; start++) {
      const owner = owners.get(labels.slice(start).join("."));
      if (owner) return owner;
    }
    return void 0;
  };
}
function judgeResearchedModel(modelNumber, probe) {
  if (!probe || probe.status === "error" || probe.status === 401 || probe.status === 403 || probe.status === 429) {
    return "blocked";
  }
  return fold(probe.text).includes(fold(modelNumber)) ? "found" : "missing";
}
function researchProbeTargets(answers, ownerOf) {
  const hosts = /* @__PURE__ */ new Set();
  const modelPages = /* @__PURE__ */ new Set();
  for (const { answer } of answers) {
    for (const domain of answer.domains) {
      const host = normalizeBrandDomain(domain.host);
      if (host && !isHostWithin(host, NOT_BRAND_OWNED_SITES) && !ownerOf(host)) hosts.add(host);
    }
    for (const model of answer.exampleModels) modelPages.add(model.evidenceUrl);
  }
  return { hosts: [...hosts], modelPages: [...modelPages] };
}
function namesBrand(text, brandName) {
  const page = fold(text);
  const firstWord = fold(brandName.split(/\s+/)[0] ?? "");
  return page.includes(fold(brandName)) || firstWord.length >= 4 && page.includes(firstWord);
}
function siteOf(host) {
  const labels = host.split(".");
  const secondLevel = labels.at(-2) ?? "";
  const countrySecondLevel = (labels.at(-1)?.length ?? 0) === 2 && ["co", "com", "org", "net", "ac"].includes(secondLevel);
  return labels.slice(countrySecondLevel ? -3 : -2).join(".");
}
function fold(value) {
  return value.toUpperCase().replace(/[^\p{L}\p{N}]/gu, "");
}
function planBrandResearchProposal(input) {
  const normalized = input.answers.map((answer2) => normalizeAnswer(answer2, input.name, input.catalog));
  const chosen = chooseDecision(normalized);
  if (!chosen) return void 0;
  const agreement = normalized.length === 1 ? "single" : normalized.every((n) => n.key === chosen.key) ? "agreed" : "split";
  const brandKeys = brandKeysOf(chosen, input);
  const pageBrand = chosen.brandName ?? targetName(chosen, input.catalog) ?? input.name;
  const { answer } = chosen;
  return {
    decision: chosen.decision,
    agreement,
    ...chosen.brandName && { brandName: chosen.brandName },
    ...chosen.targetBrandId && { targetBrandId: chosen.targetBrandId },
    ...chosen.targetBrandName && { targetBrandName: chosen.targetBrandName },
    ...chosen.lineName && { lineName: chosen.lineName },
    ...answer.notABrandReason && { notABrandReason: answer.notABrandReason },
    labelKind: answer.labelKind,
    summary: answer.summary,
    owner: {
      ...answer.owner.company && { company: answer.owner.company },
      ...answer.owner.country && { country: answer.owner.country },
      ...answer.owner.history && { history: answer.owner.history }
    },
    ...answer.website && { website: answer.website },
    domains: plannedDomains(normalized, input, pageBrand, chosen.targetBrandId),
    aliases: plannedAliases(normalized, input, brandKeys, chosen.targetBrandId),
    otherUsesOfName: plannedOtherUses(normalized),
    exampleModels: plannedModels(normalized, input.probes),
    alternatives: agreement === "split" ? normalized.map((n) => ({
      provider: n.provider,
      decision: n.decision,
      brand: n.brandName ?? targetName(n, input.catalog) ?? input.name
    })) : []
  };
}
function normalizeAnswer({ provider, answer }, name, catalog) {
  const target = answer.targetBrand?.trim() || void 0;
  const targetBrandId = target ? resolveCatalogBrandId(catalog, target) : void 0;
  const base = { provider, answer };
  if (answer.decision === "new-brand" || answer.decision === "alias-of" && !targetBrandId) {
    const brandName = (answer.decision === "new-brand" ? answer.brandName?.trim() : target) || displayOf(name);
    const existing = resolveCatalogBrandId(catalog, brandName);
    if (existing) return { ...base, decision: "alias-of", targetBrandId: existing, key: `alias-of:${existing}` };
    return { ...base, decision: "new-brand", brandName, key: `new-brand:${normalizeBrandName7(brandName).key}` };
  }
  if (answer.decision === "alias-of") {
    return { ...base, decision: "alias-of", targetBrandId, key: `alias-of:${targetBrandId}` };
  }
  if (answer.decision === "line-of") {
    const lineName = answer.lineName?.trim() || displayOf(name);
    const brand = targetBrandId ?? normalizeBrandName7(target ?? "").key;
    return {
      ...base,
      decision: "line-of",
      lineName,
      ...targetBrandId ? { targetBrandId } : target && { targetBrandName: target },
      key: `line-of:${brand}:${normalizeBrandName7(lineName).key}`
    };
  }
  return {
    ...base,
    decision: "not-a-brand",
    ...targetBrandId && { targetBrandId },
    ...target && !targetBrandId && { targetBrandName: target },
    key: "not-a-brand"
  };
}
function chooseDecision(normalized) {
  const votes = /* @__PURE__ */ new Map();
  for (const n of normalized) votes.set(n.key, (votes.get(n.key) ?? 0) + 1);
  return [...normalized].sort(
    (a, b) => (votes.get(b.key) ?? 0) - (votes.get(a.key) ?? 0) || b.answer.confidence - a.answer.confidence
  )[0];
}
function brandKeysOf(chosen, input) {
  const keys = /* @__PURE__ */ new Set([normalizeBrandName7(input.name).key]);
  if (chosen.brandName) keys.add(normalizeBrandName7(chosen.brandName).key);
  const target = chosen.targetBrandId && input.catalog.entries.find((e) => e.entry.id === chosen.targetBrandId);
  if (target) keys.add(target.canonicalKey);
  if (chosen.targetBrandName) keys.add(normalizeBrandName7(chosen.targetBrandName).key);
  return keys;
}
function plannedDomains(normalized, input, brandName, targetBrandId) {
  const byHost = /* @__PURE__ */ new Map();
  for (const { provider, answer } of normalized) {
    for (const domain of answer.domains) {
      const host = normalizeBrandDomain(domain.host);
      if (!host) continue;
      const seen = byHost.get(host);
      if (seen) {
        if (!seen.sources.includes(provider)) seen.sources.push(provider);
        continue;
      }
      if (targetBrandId && input.ownerOf(host) === targetBrandId) continue;
      const judgment = judgeResearchedDomain(host, input.probes.get(host), brandName, input.ownerOf);
      byHost.set(host, {
        host,
        role: domain.role,
        evidenceUrl: domain.evidenceUrl,
        sources: [provider],
        verdict: judgment.verdict,
        detail: judgment.detail,
        ...judgment.ownerBrandId && { ownerBrandId: judgment.ownerBrandId }
      });
    }
  }
  return [...byHost.values()];
}
function plannedAliases(normalized, input, brandKeys, targetBrandId) {
  const pendingByKey = new Map(input.similarPending.map((p) => [normalizeBrandName7(p.brand).key, p.brand]));
  const byKey = /* @__PURE__ */ new Map();
  const add = (name, kind, provider) => {
    const key = normalizeBrandName7(name).key;
    if (!key) return;
    const seen = byKey.get(key);
    if (seen) {
      if (!seen.sources.includes(provider)) seen.sources.push(provider);
      return;
    }
    const takenBy = resolveCatalogBrandId(input.catalog, name);
    byKey.set(key, {
      name,
      kind,
      sources: [provider],
      ...brandKeys.has(key) && { noop: true },
      ...takenBy && takenBy !== targetBrandId && !brandKeys.has(key) && { takenBy }
    });
  };
  for (const { provider, answer } of normalized) {
    for (const name of answer.samePendingRequests) {
      const pending = pendingByKey.get(normalizeBrandName7(name).key);
      if (pending) add(pending, "pending-request", provider);
    }
    for (const alias of answer.aliases) add(alias.name, alias.kind, provider);
  }
  return [...byKey.values()];
}
function plannedOtherUses(normalized) {
  const byCompany = /* @__PURE__ */ new Map();
  for (const { provider, answer } of normalized) {
    for (const use of answer.otherUsesOfName) {
      const key = fold(use.company);
      const seen = byCompany.get(key);
      if (seen) {
        if (!seen.sources.includes(provider)) seen.sources.push(provider);
      } else {
        byCompany.set(key, { company: use.company, products: use.products, sources: [provider] });
      }
    }
  }
  return [...byCompany.values()];
}
function plannedModels(normalized, probes) {
  const byNumber = /* @__PURE__ */ new Map();
  for (const { provider, answer } of normalized) {
    for (const model of answer.exampleModels) {
      const key = fold(model.modelNumber);
      if (!key) continue;
      const check = judgeResearchedModel(model.modelNumber, probes.get(model.evidenceUrl));
      const seen = byNumber.get(key);
      if (seen) {
        if (!seen.sources.includes(provider)) seen.sources.push(provider);
        if (seen.check !== "found" && check === "found")
          Object.assign(seen, { check, evidenceUrl: model.evidenceUrl });
        continue;
      }
      byNumber.set(key, { ...model, sources: [provider], check });
    }
  }
  return [...byNumber.values()];
}
function targetName(n, catalog) {
  if (n.targetBrandName) return n.targetBrandName;
  return catalog.entries.find((entry) => entry.entry.id === n.targetBrandId)?.entry.name;
}
function displayOf(name) {
  return normalizeBrandName7(name).display || name.trim();
}

// src/buildBrandSearchIndex.ts
import { isMeaningfulBrandName as isMeaningfulBrandName3 } from "@rock/util";
function buildBrandSearchIndex(brands, sharedNameplates, now) {
  const table = sharedNameplateEntries(sharedNameplates);
  const entries = brands.filter(
    (brand) => brand.reviewStatus !== "junk" && !brand.mergedInto && isMeaningfulBrandName3(brand.canonicalName) && !isSharedNameplateKey(brand.canonicalKey, table)
  ).sort(compareForIndex).map(toEntry);
  return {
    object: "brand-search-index",
    schema: 1,
    version: now,
    entries,
    sharedNameplates: table
  };
}
function compareForIndex(a, b) {
  const reviewed = Number(b.reviewStatus === "reviewed") - Number(a.reviewStatus === "reviewed");
  return reviewed !== 0 ? reviewed : a.canonicalName.localeCompare(b.canonicalName);
}
function toEntry(brand) {
  const aliases = brand.aliases.filter((alias) => alias !== brand.canonicalName && alias !== brand.displayName);
  return {
    id: brand.id,
    name: brand.displayName || brand.canonicalName,
    canonicalName: brand.canonicalName,
    ...aliases.length > 0 ? { aliases } : {},
    ...brand.lines?.length ? { lines: brand.lines.map((line) => ({ id: line.id, displayName: line.displayName })) } : {}
  };
}

// src/liveBrand.ts
var MAX_BRAND_MERGE_HOPS = 5;
function liveBrandStep(brand) {
  if (!brand) return void 0;
  if (brand.mergedInto) return { next: brand.mergedInto };
  return brand.reviewStatus === "junk" ? void 0 : { live: brand };
}
function followLiveBrand(brandId, lookup) {
  let id = brandId;
  for (let hops = 0; hops <= MAX_BRAND_MERGE_HOPS; hops++) {
    const step = liveBrandStep(lookup(id));
    if (!step) return void 0;
    if ("live" in step) return step.live;
    id = step.next;
  }
  return void 0;
}
async function resolveLiveBrand(brandId, load) {
  let id = brandId;
  for (let hops = 0; hops <= MAX_BRAND_MERGE_HOPS; hops++) {
    const step = liveBrandStep(await load(id));
    if (!step) return void 0;
    if ("live" in step) return step.live;
    id = step.next;
  }
  return void 0;
}

// src/pickBrandAssets.ts
var BRAND_LOGO_URL_WIDTH = 800;
var BRAND_LOGO_THUMB_WIDTH = 240;
var BRAND_ICON_URL_WIDTH = 400;
var BRAND_ICON_THUMB_WIDTH = 96;
var RASTER_FORMAT_PREFERENCE = ["png", "webp", "jpeg", "jpg"];
function brandAssetId(domain, slot, logoIndex) {
  return `brandfetch-${domain}-${slot}-a${logoIndex}`;
}
function pickBrandAssets(payload) {
  const logos = payload.logos ?? [];
  return {
    logo: pickLogo(logos),
    logoOnDark: pickLogoOnDark(logos),
    icon: pickIcon(logos)
  };
}
function indexed(logos) {
  return logos.map((logo, logoIndex) => ({ logo, logoIndex }));
}
function pickLogo(logos) {
  const wordmarks = indexed(logos).filter(({ logo }) => logo.type === "logo");
  const fallbacks = indexed(logos).filter(({ logo }) => logo.type === "other" || logo.type === "symbol");
  return pickFirst([
    wordmarks.find(({ logo }) => logo.theme === "dark"),
    wordmarks.find(({ logo }) => logo.theme == null),
    wordmarks.find(({ logo }) => logo.theme === "light"),
    fallbacks.find(({ logo }) => logo.theme === "dark"),
    fallbacks.find(({ logo }) => logo.theme == null),
    fallbacks.find(({ logo }) => logo.theme === "light")
  ]);
}
function pickLogoOnDark(logos) {
  const lightWordmark = indexed(logos).find(({ logo }) => logo.type === "logo" && logo.theme === "light");
  return lightWordmark ? entryToPick(lightWordmark) : void 0;
}
function pickIcon(logos) {
  const icons = indexed(logos).filter(({ logo }) => logo.type === "icon");
  return pickFirst([
    icons.find(({ logo }) => logo.theme === "dark"),
    icons.find(({ logo }) => logo.theme == null),
    icons.find(({ logo }) => logo.theme === "light")
  ]);
}
function pickFirst(entries) {
  for (const entry of entries) {
    const pick = entry && entryToPick(entry);
    if (pick) return pick;
  }
  return void 0;
}
function entryToPick({ logo, logoIndex }) {
  const raster = bestRasterFormat(logo.formats);
  const svg = logo.formats?.find((f) => f.format === "svg");
  if (!raster && !svg) return void 0;
  const chosen = raster ?? svg;
  return {
    logoIndex,
    transparent: chosen?.background === "transparent",
    svgSrc: svg?.src,
    raster: raster ? { src: raster.src, width: raster.width, height: raster.height } : void 0
  };
}
function bestRasterFormat(formats) {
  const usable = (formats ?? []).filter((f) => f.format !== "svg" && !!f.width && !!f.height);
  return usable.sort((a, b) => rasterRank(a.format) - rasterRank(b.format))[0];
}
function rasterRank(format) {
  const i = RASTER_FORMAT_PREFERENCE.indexOf(format ?? "");
  return i === -1 ? RASTER_FORMAT_PREFERENCE.length : i;
}
function brandAssetCandidates(payload) {
  const candidates = [];
  (payload.logos ?? []).forEach((logo, logoIndex) => {
    const theme = logo.theme === "dark" || logo.theme === "light" ? logo.theme : null;
    for (const format of logo.formats ?? []) {
      candidates.push({
        logoIndex,
        type: logo.type ?? "other",
        theme,
        format: format.format ?? "unknown",
        src: format.src,
        width: format.width ?? void 0,
        height: format.height ?? void 0,
        transparent: format.background === "transparent"
      });
    }
  });
  return candidates;
}
function brandfetchRasterUrl(src, width) {
  let url;
  try {
    url = new URL(src);
  } catch {
    return src;
  }
  if (url.hostname !== "cdn.brandfetch.io") return src;
  const segments = url.pathname.split("/").filter(Boolean);
  const [brandId, ...rest] = segments;
  if (!brandId) return src;
  while (rest.length >= 2 && (rest[0] === "w" || rest[0] === "h") && /^\d+$/.test(rest[1])) {
    rest.splice(0, 2);
  }
  const last = rest.length - 1;
  if (last >= 0) rest[last] = rest[last].replace(/\.[a-z0-9]+$/i, ".png");
  url.pathname = `/${[brandId, "w", `${width}`, ...rest].join("/")}`;
  return url.toString();
}
function parseSvgAspect(svgText) {
  const match = svgText.match(/viewBox\s*=\s*["']\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*["']/i);
  if (!match) return void 0;
  const width = Number(match[3]);
  const height = Number(match[4]);
  if (!width || !height) return void 0;
  return width / height;
}

// src/planBrandMerge.ts
import { confusionKey as confusionKey3, dedupeStrings as dedupeStrings4, normalizeBrandName as normalizeBrandName8 } from "@rock/util";
function planBrandMerge({
  loser,
  winner,
  loserAliasDocs,
  sharedNameplates,
  asLine,
  now
}) {
  if (loser.id === winner.id) return { refusal: "A brand cannot be merged into itself." };
  if (winner.mergedInto) {
    return { refusal: `${winner.canonicalName} was itself merged; merge into the brand it points at.` };
  }
  if (winner.reviewStatus === "junk") {
    return { refusal: `${winner.canonicalName} is marked junk; merge into a real brand.` };
  }
  if (loser.mergedInto && loser.mergedInto !== winner.id) {
    return { refusal: `${loser.canonicalName} was already merged into another brand.` };
  }
  if (isSharedNameplateKey(loser.canonicalKey, sharedNameplates) || isSharedNameplateKey(winner.canonicalKey, sharedNameplates)) {
    return { refusal: "A shared nameplate is split, not merged \u2014 use the qualified brands." };
  }
  if (isSharedNameplateSide(loser.id, sharedNameplates)) {
    return {
      refusal: `${loser.canonicalName} is a side of a shared nameplate; edit the nameplate before merging it.`
    };
  }
  const recordedLineId = loser.mergedInto === winner.id ? loser.mergedAsLineId : void 0;
  const asLineId = asLine ? brandLineIdOn(winner, asLine.displayName, sharedNameplates) : void 0;
  if (recordedLineId && asLineId !== recordedLineId) {
    return { refusal: `This merge started as the ${recordedLineId} line; re-run it as that line.` };
  }
  if (!recordedLineId && loser.mergedInto === winner.id && asLine) {
    return { refusal: "This merge started as a plain merge; re-run it without a line." };
  }
  if (asLine && loser.lines?.length) {
    return { refusal: `${loser.canonicalName} has lines of its own; a line can't hold lines. Merge it plainly.` };
  }
  const lineAddition = asLine ? planBrandLineAddition(winner, { displayName: asLine.displayName, now }, sharedNameplates) : void 0;
  if (lineAddition && "refusal" in lineAddition && !lineAddition.existingLine) {
    return { refusal: lineAddition.refusal };
  }
  const line = lineAddition && ("plan" in lineAddition ? lineAddition.plan.line : lineAddition.existingLine);
  const loserDocs = loserAliasDocs.filter((doc) => doc.brandId === loser.id);
  const qualifying = line ? [...brandQualifyingKeys(winner, sharedNameplates)] : void 0;
  const carriedDocs = qualifying ? loserDocs.filter((doc) => qualifying.some((key) => confusionKey3(doc.id).includes(confusionKey3(key)))) : loserDocs;
  const carriedKeys = new Set(carriedDocs.map((doc) => doc.id));
  const lineDocs = line ? brandLineKeyDocs(winner, line.id, sharedNameplates, now) : [];
  const aliasDocs = [
    ...carriedDocs.map((doc) => ({
      ...doc,
      brandId: winner.id,
      canonicalKey: winner.canonicalKey,
      ...line ? { lineId: line.id } : {},
      updatedAt: now
    })),
    ...lineDocs.filter((doc) => !carriedKeys.has(doc.id))
  ];
  const writtenKeys = new Set(aliasDocs.map((doc) => doc.id));
  const deleteAliasKeys = qualifying ? loserDocs.filter((doc) => !writtenKeys.has(doc.id)).map((doc) => doc.id) : [];
  const winnerPatch = line ? lineAddition && "plan" in lineAddition ? { lines: lineAddition.plan.brandLines } : {} : winnerPatches(loser, winner, sharedNameplates);
  return {
    plan: {
      aliasDocs,
      deleteAliasKeys,
      winnerPatch: { ...winnerPatch, updatedAt: now },
      loserMark: {
        mergedInto: winner.id,
        mergedAt: now,
        ...line ? { mergedAsLineId: line.id } : {},
        updatedAt: now
      },
      loserPatch: { reviewStatus: "junk", statusChangedAt: now, updatedAt: now },
      ...line ? { line } : {}
    }
  };
}
function winnerPatches(loser, winner, sharedNameplates) {
  const patch = {};
  const known = /* @__PURE__ */ new Set([winner.canonicalKey, ...winner.aliases.map((alias) => normalizeBrandName8(alias).key)]);
  const carried = [loser.canonicalName, ...loser.aliases].filter((name) => {
    const key = normalizeBrandName8(name).key;
    if (!key || known.has(key) || isSharedNameplateKey(key, sharedNameplates)) return false;
    known.add(key);
    return true;
  });
  if (carried.length) patch.aliases = [...winner.aliases, ...carried];
  const owned = new Set(brandOwnedDomains(winner));
  const loserResearched = new Set(loser.researchedDomains ?? []);
  const listed = brandOwnedDomains(loser).filter((domain) => !owned.has(domain) && !loserResearched.has(domain));
  if (listed.length) patch.domains = dedupeStrings4([...winner.domains ?? [], ...listed]);
  const researched = [...loserResearched].filter((domain) => !owned.has(domain));
  if (researched.length)
    patch.researchedDomains = dedupeStrings4([...winner.researchedDomains ?? [], ...researched]);
  const ownedAfterMerge = /* @__PURE__ */ new Set([...owned, ...brandOwnedDomains(loser)]);
  const trusted = dedupeStrings4([...brandTrustedDomains(winner), ...brandTrustedDomains(loser)]).filter(
    (domain) => !ownedAfterMerge.has(domain)
  );
  if (!sameList(trusted, winner.trustedDomains ?? [])) patch.trustedDomains = trusted;
  const lines = [
    ...winner.lines ?? [],
    ...(loser.lines ?? []).filter((line) => !winner.lines?.some((own) => own.id === line.id))
  ];
  if (lines.length !== (winner.lines ?? []).length) patch.lines = lines;
  if (!winner.website && loser.website) patch.website = loser.website;
  if (!winner.description && loser.description) patch.description = loser.description;
  if (!winner.primaryColor && loser.primaryColor) patch.primaryColor = loser.primaryColor;
  return patch;
}
function sameList(a, b) {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}
export {
  BRAND_ICON_THUMB_WIDTH,
  BRAND_ICON_URL_WIDTH,
  BRAND_LOGO_THUMB_WIDTH,
  BRAND_LOGO_URL_WIDTH,
  BRAND_RESEARCH_HINT_LIMIT,
  MAX_BRAND_MERGE_HOPS,
  NO_SHARED_NAMEPLATES,
  applyBrandRequestSighting,
  brandAlreadyNaming,
  brandAssetCandidates,
  brandAssetId,
  brandDomainOwnerLookup,
  brandIdentifyingNames,
  brandLineIdOn,
  brandLineKeyDocs,
  brandOwnedDomains,
  brandPickNamesText,
  brandPickStands,
  brandQualifyingKeys,
  brandRequestIdFor,
  brandResearchHints,
  brandTrustedDomains,
  brandfetchRasterUrl,
  buildBrandSearchIndex,
  catalogRequestIdFor,
  foldBrandCatalog,
  followLiveBrand,
  isBrandOwnedHost,
  isBrandTrustedHost,
  isCatalogableBrandName,
  isHostWithin,
  isSharedNameplateKey,
  isSharedNameplateName,
  isSharedNameplateSide,
  judgeResearchedDomain,
  judgeResearchedModel,
  lineNameFromAlias,
  liveBrandStep,
  normalizeBrandDomain,
  parseSvgAspect,
  pickBrandAssets,
  planBrandAliasAddition,
  planBrandAliasRemoval,
  planBrandCreation,
  planBrandLineAddition,
  planBrandLineRemoval,
  planBrandMerge,
  planBrandResearchProposal,
  planSharedNameplate,
  planSharedNameplateDenials,
  researchProbeTargets,
  resolveCatalogBrandId,
  resolveExactBrandId,
  resolveLiveBrand,
  routeSharedNameplate,
  searchBrands,
  sharedNameplateEntries,
  sharedNameplateForKey,
  similarPendingRequests,
  suggestBrandRequestResolution,
  withBrandDomainAdded,
  withBrandDomainPinned,
  withBrandDomainRemoved,
  withResearchedDomainRemoved,
  withResearchedDomainsAdded,
  withTrustedDomainAdded,
  withTrustedDomainRemoved
};
//# sourceMappingURL=index.js.map